import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { ChevronLeft, ChevronRight, Copy, Loader2, X } from 'lucide-react';
import { fetchLogById, fetchLogs } from '../../services/logsService';
import type { AppLogRecord, LogsListResponse } from '../../types/AppLog';
import { adm } from '../admin/adminTheme';

function JsonBlock({ value, title }: { value: unknown; title: string }) {
  const text =
    value === undefined || value === null
      ? '—'
      : typeof value === 'string'
        ? value
        : JSON.stringify(value, null, 2);

  const copy = () => {
    void navigator.clipboard.writeText(text);
  };

  return (
    <div className={`${adm.panel} overflow-hidden`}>
      <div className={`${adm.panelHeader} flex items-center justify-between`}>
        <span className={adm.panelTitle}>{title}</span>
        <button
          type="button"
          onClick={copy}
          className="rounded-lg p-1.5 text-[#7d8696] transition hover:bg-white/[0.06] hover:text-[#e4e6ed]"
          aria-label="Kopyala"
        >
          <Copy className="h-3.5 w-3.5" />
        </button>
      </div>
      <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-words p-3 font-mono text-[11px] leading-relaxed text-[#c8cdd8]">
        {text}
      </pre>
    </div>
  );
}

function levelBadge(level: string) {
  const base =
    'inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide';
  if (level === 'error') return `${base} bg-red-950/80 text-red-300 border border-red-900/60`;
  if (level === 'warn') return `${base} bg-amber-950/80 text-amber-200 border border-amber-900/60`;
  if (level === 'debug') return `${base} bg-neutral-900 text-neutral-400 border border-[#2e3742]`;
  return `${base} bg-[#1a2f4a]/60 text-[#8ab8ff] border border-[#2e3742]`;
}

function statusBadge(code: number | null | undefined) {
  if (code == null) return <span className="text-[#4b5563]">—</span>;
  const base = 'inline-flex px-1.5 py-0.5 rounded text-[10px] font-mono font-medium';
  if (code >= 500) return <span className={`${base} bg-red-950/60 text-red-300`}>{code}</span>;
  if (code >= 400) return <span className={`${base} bg-amber-950/60 text-amber-200`}>{code}</span>;
  return <span className={`${base} bg-emerald-950/60 text-emerald-300`}>{code}</span>;
}

type Pill = '' | 'errors' | 'warnings' | 'requests' | '4xx' | '5xx' | 'slow';

type Filters = {
  q: string;
  level: string;
  type: string;
  method: string;
  route: string;
  statusCode: string;
  dateFrom: string;
  dateTo: string;
  pill: Pill;
  slowMs: string;
};

const emptyFilters: Filters = {
  q: '',
  level: '',
  type: '',
  method: '',
  route: '',
  statusCode: '',
  dateFrom: '',
  dateTo: '',
  pill: '',
  slowMs: '',
};

function isoToDatetimeLocalValue(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export const Logs: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<LogsListResponse | null>(null);
  const [page, setPage] = useState(0);
  const [draft, setDraft] = useState<Filters>(emptyFilters);
  const [applied, setApplied] = useState<Filters>(emptyFilters);
  const [selected, setSelected] = useState<AppLogRecord | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    const routeFilter = searchParams.get('route');
    const pillRaw = searchParams.get('pill');
    const slowMsParam = searchParams.get('slowMs');
    const dateFromParam = searchParams.get('dateFrom');
    const dateToParam = searchParams.get('dateTo');
    const qParam = searchParams.get('q');

    const pillOk = new Set(['errors', 'warnings', 'requests', '4xx', '5xx', 'slow']);
    const pill: Pill = pillRaw && pillOk.has(pillRaw) ? (pillRaw as Pill) : '';

    const next: Partial<Filters> = {};
    if (routeFilter !== null) next.route = routeFilter;
    if (pillRaw !== null) next.pill = pill;
    if (slowMsParam !== null) next.slowMs = slowMsParam;
    if (dateFromParam !== null && dateFromParam !== '') next.dateFrom = isoToDatetimeLocalValue(dateFromParam);
    if (dateToParam !== null && dateToParam !== '') next.dateTo = isoToDatetimeLocalValue(dateToParam);
    if (qParam !== null) next.q = qParam;

    if (Object.keys(next).length === 0) return;

    setDraft((d) => ({ ...d, ...next }));
    setApplied((a) => ({ ...a, ...next }));
    setPage(0);
  }, [searchParams]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchLogs({
        page,
        pageSize: 50,
        q: applied.q || undefined,
        level: applied.level || undefined,
        type: applied.type || undefined,
        method: applied.method || undefined,
        route: applied.route || undefined,
        statusCode: applied.statusCode || undefined,
        dateFrom: applied.dateFrom || undefined,
        dateTo: applied.dateTo || undefined,
        pill: applied.pill || undefined,
        slowMs: applied.pill === 'slow' ? applied.slowMs || '1000' : undefined,
        stats: 1,
      });
      setData(res.data);
    } catch {
      setError('Kayıtlar yüklenemedi. Oturum veya sunucu yetkisini kontrol edin.');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [page, applied]);

  useEffect(() => {
    void load();
  }, [load]);

  const openDetail = async (row: AppLogRecord) => {
    setSelected(row);
    setDetailLoading(true);
    try {
      const res = await fetchLogById(row.id);
      setSelected(res.data);
    } catch {
      setSelected(row);
    } finally {
      setDetailLoading(false);
    }
  };

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;
  const items = data?.items ?? [];

  return (
    <div className="flex min-h-[60vh] flex-col gap-8 text-[#e4e6ed]">
      <header className="space-y-1">
        <h2 className="text-xl font-semibold tracking-tight text-[#f4f5f8] sm:text-2xl">Logs</h2>
        <p className="max-w-2xl text-sm leading-relaxed text-[#8b92a1]">
          Sunucu ve uygulama kayıtlarını arayın, süzün ve satır detayını açın.
        </p>
      </header>

      <div className="flex flex-wrap gap-2">
        {(
          [
            ['errors', 'Errors'],
            ['warnings', 'Warnings'],
            ['requests', 'Requests'],
            ['4xx', '4xx'],
            ['5xx', '5xx'],
            ['slow', 'Slow'],
          ] as const
        ).map(([k, label]) => (
          <button
            key={k}
            type="button"
            onClick={() => {
              const next = draft.pill === k ? '' : k;
              setDraft((d) => ({ ...d, pill: next as Pill }));
              setApplied((a) => ({ ...a, pill: next as Pill }));
              setPage(0);
            }}
            className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-all ${
              applied.pill === k ? adm.pillActive : adm.pillInactive
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {data?.stats && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {[
            ['Bugün · hata', data.stats.errorsToday],
            ['Bugün · uyarı', data.stats.warningsToday],
            ['Bugün · yavaş istek', data.stats.slowToday],
          ].map(([label, val]) => (
            <div key={String(label)} className={adm.statCard}>
              <p className={adm.statLabel}>{label}</p>
              <p className={`${adm.statValue} mt-1`}>{val}</p>
            </div>
          ))}
        </div>
      )}

      <div className={`${adm.panel} space-y-4 p-4 sm:p-5`}>
        <p className={adm.sectionLabel}>Filtreler</p>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
          <input
            type="search"
            placeholder="Ara (mesaj, route, trace…)"
            value={draft.q}
            onChange={(e) => setDraft((d) => ({ ...d, q: e.target.value }))}
            className={adm.input}
          />
          <select
            value={draft.level}
            onChange={(e) => setDraft((d) => ({ ...d, level: e.target.value }))}
            className={adm.input}
          >
            <option value="">Level</option>
            <option value="info">info</option>
            <option value="warn">warn</option>
            <option value="error">error</option>
            <option value="debug">debug</option>
          </select>
          <select
            value={draft.type}
            onChange={(e) => setDraft((d) => ({ ...d, type: e.target.value }))}
            className={adm.input}
          >
            <option value="">Type</option>
            <option value="app">app</option>
            <option value="request">request</option>
            <option value="response">response</option>
            <option value="exception">exception</option>
            <option value="audit">audit</option>
          </select>
          <input
            type="text"
            placeholder="Method (GET, POST…)"
            value={draft.method}
            onChange={(e) => setDraft((d) => ({ ...d, method: e.target.value }))}
            className={adm.input}
          />
          <input
            type="text"
            placeholder="Route içerir"
            value={draft.route}
            onChange={(e) => setDraft((d) => ({ ...d, route: e.target.value }))}
            className={adm.input}
          />
          <input
            type="text"
            placeholder="Status code"
            value={draft.statusCode}
            onChange={(e) => setDraft((d) => ({ ...d, statusCode: e.target.value }))}
            className={adm.input}
          />
          <input
            type="datetime-local"
            value={draft.dateFrom}
            onChange={(e) => setDraft((d) => ({ ...d, dateFrom: e.target.value }))}
            className={adm.input}
          />
          <input
            type="datetime-local"
            value={draft.dateTo}
            onChange={(e) => setDraft((d) => ({ ...d, dateTo: e.target.value }))}
            className={adm.input}
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => {
              setApplied({ ...draft });
              setPage(0);
            }}
            className={adm.btnPrimary}
          >
            Uygula
          </button>
          <button
            type="button"
            onClick={() => {
              setDraft(emptyFilters);
              setApplied(emptyFilters);
              setPage(0);
            }}
            className={adm.btnGhost}
          >
            Sıfırla
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-900/50 bg-red-950/30 px-4 py-3 text-sm text-red-200">{error}</div>
      )}

      <div className={`${adm.panel} overflow-hidden`}>
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-24 text-sm text-[#8b92a1]">
            <Loader2 className="h-5 w-5 animate-spin text-[#818cf8]" />
            Yükleniyor…
          </div>
        ) : items.length === 0 ? (
          <div className="py-16 text-center text-sm text-[#6b7280]">Kayıt yok</div>
        ) : (
          <div className={adm.tableWrap}>
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-white/[0.06]">
                  <th className={`${adm.tableHead} px-4 py-3`}>Zaman</th>
                  <th className={`${adm.tableHead} px-4 py-3`}>Lvl</th>
                  <th className={`${adm.tableHead} px-4 py-3`}>Tip</th>
                  <th className={`${adm.tableHead} px-4 py-3`}>Met</th>
                  <th className={`${adm.tableHead} px-4 py-3`}>Route</th>
                  <th className={`${adm.tableHead} px-4 py-3`}>St</th>
                  <th className={`${adm.tableHead} px-4 py-3`}>ms</th>
                  <th className={`${adm.tableHead} px-4 py-3`}>Özet</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr
                    key={row.id}
                    className={`${adm.tableRow} cursor-pointer`}
                    onClick={() => void openDetail(row)}
                  >
                    <td className={`${adm.tableCell} whitespace-nowrap text-[12px] text-[#9ca3b8]`}>
                      {format(new Date(row.createdAt), 'PPp', { locale: tr })}
                    </td>
                    <td className={adm.tableCell}>
                      <span className={levelBadge(row.level)}>{row.level}</span>
                    </td>
                    <td className={`${adm.tableCell} font-mono text-[11px]`}>{row.type}</td>
                    <td className={`${adm.tableCell} font-mono text-[11px]`}>{row.method ?? '—'}</td>
                    <td className={`${adm.tableCell} max-w-[200px] truncate font-mono text-[11px]`}>
                      {row.route ?? '—'}
                    </td>
                    <td className={adm.tableCell}>{statusBadge(row.statusCode)}</td>
                    <td className={`${adm.tableCell} tabular-nums`}>{row.durationMs ?? '—'}</td>
                    <td className={`${adm.tableCell} max-w-[240px] truncate text-[12px]`}>
                      {row.message ?? row.eventName ?? '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {data && data.total > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-[#8b92a1]">
            Sayfa {page + 1} / {totalPages} · Toplam {data.total}
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={page <= 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              className={`inline-flex items-center gap-1 rounded-lg border px-3 py-2 text-sm ${
                page <= 0
                  ? 'cursor-not-allowed border-white/[0.06] text-[#4b5563]'
                  : 'border-white/[0.1] text-[#e4e6ed] hover:bg-white/[0.04]'
              }`}
            >
              <ChevronLeft className="h-4 w-4" />
              Önceki
            </button>
            <button
              type="button"
              disabled={page >= totalPages - 1}
              onClick={() => setPage((p) => p + 1)}
              className={`inline-flex items-center gap-1 rounded-lg border px-3 py-2 text-sm ${
                page >= totalPages - 1
                  ? 'cursor-not-allowed border-white/[0.06] text-[#4b5563]'
                  : 'border-white/[0.1] text-[#e4e6ed] hover:bg-white/[0.04]'
              }`}
            >
              Sonraki
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {selected && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/50 backdrop-blur-sm">
          <div
            className={`flex h-full w-full max-w-lg flex-col border-l border-white/[0.08] bg-[#0a0c11] shadow-2xl`}
          >
            <div className="flex items-center justify-between border-b border-white/[0.06] px-4 py-3">
              <h3 className="text-sm font-semibold text-[#f4f5f8]">Log detayı</h3>
              <button
                type="button"
                onClick={() => setSelected(null)}
                className="rounded-lg p-2 text-[#8b92a1] hover:bg-white/[0.06] hover:text-[#e4e6ed]"
                aria-label="Kapat"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {detailLoading ? (
                <div className="flex flex-col items-center gap-2 py-16 text-sm text-[#8b92a1]">
                  <Loader2 className="h-6 w-6 animate-spin text-[#818cf8]" />
                  Yükleniyor…
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="space-y-2 text-sm">
                    <p>
                      <span className="text-[#6b7280]">ID</span>{' '}
                      <span className="font-mono text-[12px] text-[#c8cdd8]">{selected.id}</span>
                    </p>
                    <p>
                      <span className="text-[#6b7280]">Zaman</span>{' '}
                      {format(new Date(selected.createdAt), 'PPpp', { locale: tr })}
                    </p>
                    <p className="flex flex-wrap items-center gap-2">
                      <span className="text-[#6b7280]">Level</span>
                      <span className={levelBadge(selected.level)}>{selected.level}</span>
                    </p>
                    <p>
                      <span className="text-[#6b7280]">Tip</span>{' '}
                      <span className="font-mono">{selected.type}</span>
                    </p>
                    <p>
                      <span className="text-[#6b7280]">Route</span>{' '}
                      <span className="font-mono text-[12px] break-all">{selected.route ?? '—'}</span>
                    </p>
                    <p>
                      <span className="text-[#6b7280]">Status</span> {statusBadge(selected.statusCode)}
                    </p>
                    <p>
                      <span className="text-[#6b7280]">ms</span>{' '}
                      <span className="tabular-nums">{selected.durationMs ?? '—'}</span>
                    </p>
                  </div>
                  <JsonBlock value={selected.message} title="Mesaj" />
                  <JsonBlock value={selected.requestBody} title="İstek gövdesi" />
                  <JsonBlock value={selected.responseBody} title="Yanıt gövdesi" />
                  <JsonBlock value={selected.metadata} title="Metadata" />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
